/**
 * Hindi script to English letters (Hinglish), shared by the website toolbar (live speech-to-text)
 * and the worker (final transcripts of voice discussions).
 */
/**
 * English words of web design as Deepgram writes them in Hindi script (people say them in
 * English, inside Hindi sentences): written back as the English word, so "लोगो चेंज करो" reads
 * "logo change karo" and becomes "Change the logo." Looked up without the nukta dot.
 */
const ENGLISH: Record<string, string> = {
  चेंज: 'change', चेन्ज: 'change', रिमूव: 'remove', रीमूव: 'remove', ऐड: 'add', एड: 'add', डिलीट: 'delete', रिप्लेस: 'replace', अपडेट: 'update',
  अलाइन: 'align', अलाइनमेंट: 'alignment', सेंटर: 'center', लिंक: 'link', मूव: 'move', शिफ्ट: 'shift', हाइड: 'hide', शो: 'show', फिक्स: 'fix',
  बटन: 'button', बटन्स: 'buttons', मेनू: 'menu', मेन्यू: 'menu', लोगो: 'logo', इमेज: 'image', इमेजेस: 'images', फोटो: 'photo', बैनर: 'banner',
  हेडर: 'header', हैडर: 'header', फुटर: 'footer', फूटर: 'footer', सेक्शन: 'section', पेज: 'page', होमपेज: 'homepage', टेक्स्ट: 'text',
  हेडिंग: 'heading', टाइटल: 'title', फॉन्ट: 'font', फोंट: 'font', साइज: 'size', कलर: 'colour', कलर्स: 'colours', बैकग्राउंड: 'background',
  बॉर्डर: 'border', शैडो: 'shadow', पैडिंग: 'padding', मार्जिन: 'margin', स्पेस: 'space', स्पेसिंग: 'spacing', गैप: 'gap', लेफ्ट: 'left',
  राइट: 'right', टॉप: 'top', बॉटम: 'bottom', मोबाइल: 'mobile', डेस्कटॉप: 'desktop', टैबलेट: 'tablet', स्लाइडर: 'slider', आइकन: 'icon',
  आइकॉन: 'icon', प्रोडक्ट: 'product', प्रोडक्ट्स: 'products', कलेक्शन: 'collection', कार्ट: 'cart', चेकआउट: 'checkout', प्राइस: 'price',
  डिजाइन: 'design', डिज़ाइन: 'design', लेआउट: 'layout', स्क्रॉल: 'scroll', क्लिक: 'click', होवर: 'hover', ड्रॉपडाउन: 'dropdown', पॉपअप: 'popup',
  फॉर्म: 'form', फील्ड: 'field', ईमेल: 'email', सर्च: 'search', फिल्टर: 'filter', कार्ड: 'card', ग्रिड: 'grid', वीडियो: 'video', बोल्ड: 'bold',
  नेवबार: 'navbar', नेविगेशन: 'navigation', वेबसाइट: 'website', लाइन: 'line', स्क्रीन: 'screen', सेटिंग: 'setting', प्लीज: 'please', प्लीज़: 'please',
  ओके: 'ok', नेक्स्ट: 'next', पॉइंट: 'point', स्टॉप: 'stop', रिकॉर्डिंग: 'recording',
  // Common Hindi words, spelled the way Hinglish is typed (and the instruction rules read them).
  में: 'mein', मैं: 'main', नहीं: 'nahin', है: 'hai', हैं: 'hain',
};
const noNukta = (w: string) => w.replace(/़/g, '');
const ENGLISH_PLAIN: Record<string, string> = Object.fromEntries(Object.entries(ENGLISH).map(([k, v]) => [noNukta(k), v]));

const VOWELS: Record<string, string> = { अ: 'a', आ: 'aa', इ: 'i', ई: 'ee', उ: 'u', ऊ: 'oo', ऋ: 'ri', ए: 'e', ऐ: 'ai', ओ: 'o', औ: 'au', ऑ: 'o' };
const SIGNS: Record<string, string> = { 'ा': 'a', 'ि': 'i', 'ी': 'i', 'ु': 'u', 'ू': 'u', 'ृ': 'ri', 'े': 'e', 'ै': 'ai', 'ो': 'o', 'ौ': 'au', 'ॉ': 'o', 'ॅ': 'e' };
const CONS: Record<string, string> = {
  क: 'k', ख: 'kh', ग: 'g', घ: 'gh', ङ: 'n', च: 'ch', छ: 'chh', ज: 'j', झ: 'jh', ञ: 'n', ट: 't', ठ: 'th', ड: 'd', ढ: 'dh', ण: 'n',
  त: 't', थ: 'th', द: 'd', ध: 'dh', न: 'n', प: 'p', फ: 'ph', ब: 'b', भ: 'bh', म: 'm', य: 'y', र: 'r', ल: 'l', व: 'v', श: 'sh', ष: 'sh', स: 's', ह: 'h',
};
const NUKTA: Record<string, string> = { क: 'q', ख: 'kh', ग: 'g', ज: 'z', ड: 'r', ढ: 'rh', फ: 'f' };

/**
 * Hindi in Hindi script, written the way people type Hinglish: "लोगो बदलो" → "logo badlo",
 * "नहीं" → "nahin", "ठीक करो" → "thik karo"; English design words come back as English
 * ("बटन" → "button"). Words already in English letters are left as they are.
 */
export function romanize(text: string): string {
  return text.replace(/[ऀ-ॿ]+/g, (word) => {
    const known = ENGLISH[word] ?? ENGLISH_PLAIN[noNukta(word)];
    if (known) return known;
    let out = '';
    const ch = [...word];
    for (let i = 0; i < ch.length; i++) {
      const c = ch[i]!;
      if (VOWELS[c]) {
        out += VOWELS[c];
        continue;
      }
      if (CONS[c]) {
        let base = CONS[c]!;
        if (ch[i + 1] === '़') {
          base = NUKTA[c] ?? base;
          i++;
        }
        const next = ch[i + 1];
        if (next && SIGNS[next] !== undefined) {
          out += base + SIGNS[next];
          i++;
        } else if (next === '्') {
          out += base; // halant: no vowel
          i++;
        } else {
          // The inherent "a", dropped at the end of a word (and before a vowel sign-less last letter).
          const last = i === ch.length - 1 || (i === ch.length - 2 && /[ँंः]/.test(ch[i + 1]!));
          out += last ? base : `${base}a`;
        }
        continue;
      }
      if (c === 'ं' || c === 'ँ') out += 'n';
      else if (c === 'ः') out += 'h';
      else if (/[०-९]/.test(c)) out += String(c.charCodeAt(0) - 0x0966);
      else if (c === '।') out += '.';
    }
    // Two-syllable words keep their middle "a" short: "badalo" → "badlo", "karana" → "karna".
    return out.replace(/^([^aeiou]*[aeiou][^aeiou])a([^aeiou][aeiou]?)$/, '$1$2');
  });
}
